import * as genericService from "./genericFormServices";
import type { PLOFormSixData } from "../types/PLOs/plo_six.type";
import type { SubmissionStatus } from "../types/MainType.type";

const ENDPOINT = "plo-form-six";

export interface PLOFetchResponse {
  data: PLOFormSixData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for PLO Form Six
// ---------------------------------------------------------------------------
export const mapPloFormSixBackendToFrontend = (item: any): PLOFormSixData => {
  if (!item) return {} as PLOFormSixData;

  // Extract nested form entity from backend response structure (PLO Form Six)
  const form = item.pLOFormSix || item.ploFormSix || item.formData || item;

  return {
    id: item.id || form.id || '',
    documentNumber: item.documentNumber || form.documentNumber || '',
    formType: item.formType || form.formType || '',
    
    // Core Form Six Fields
    officeId: form.officeId || item.officeId || '',
    responsibleOfficerName: form.responsibleOfficerName || '',
    lawOffice: form.lawOffice || '',

    caseNo: form.caseNo || '',
    plaintiff: form.plaintiff || '',
    defendant: form.defendant || '',
    lawSection: form.lawSection || '',
    witnessName: form.witnessName || '',
    officerName: form.officerName || '',
    interviewDate: form.interviewDate || '',
    remarks: form.remarks || '',

    // Status & Metadata
    status: (item.status || form.status || 'DRAFT') as SubmissionStatus,
    submitDate: item.submitDate || form.submitDate || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // System Dates & Audit
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory) ? item.stageHistory : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Filter Function for PLO Form Six
// ---------------------------------------------------------------------------
const ploFilterPredicate = (
  app: PLOFormSixData,
  params: PloSixFetchParams
): boolean => {
  const { searchTerm, courtSearch, partySearch, sectionSearch, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  
  // Date match against interview date or other date fields
  const matchesDate =
    !selectedDate ||
    Boolean(
      (app.interviewDate && app.interviewDate.startsWith(selectedDate)) ||
      (app.ptiFirstDate && app.ptiFirstDate.startsWith(selectedDate)) ||
      (app.ptiSecondDate && app.ptiSecondDate.startsWith(selectedDate))
    );

  const term = (searchTerm || "").toLowerCase().trim();
  const partyTerm = (partySearch || "").toLowerCase().trim();
  const courtTerm = (courtSearch || "").toLowerCase().trim();
  const sectionTerm = (sectionSearch || "").toLowerCase().trim();

  // Search across Form Six specific text fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.caseNo && app.caseNo.toLowerCase().includes(term)) ||
    Boolean(app.victimOrWitnessName && app.victimOrWitnessName.toLowerCase().includes(term)) ||
    Boolean(app.witnessName && app.witnessName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerName && app.interviewerName.toLowerCase().includes(term)) ||
    Boolean(app.officerName && app.officerName.toLowerCase().includes(term)) ||
    Boolean(app.interviewerRole && app.interviewerRole.toLowerCase().includes(term)) ||
    Boolean(app.officerOpinionName && app.officerOpinionName.toLowerCase().includes(term)) ||
    Boolean(app.remarks && app.remarks.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  const matchesPartySearch =
    !partyTerm ||
    Boolean(app.plaintiff && app.plaintiff.toLowerCase().includes(partyTerm)) ||
    Boolean(app.defendant && app.defendant.toLowerCase().includes(partyTerm));

  const matchesCourtSearch =
    !courtTerm ||
    Boolean(app.caseNo && app.caseNo.toLowerCase().includes(courtTerm));

  const matchesSectionSearch =
    !sectionTerm ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(sectionTerm));

  return Boolean(
    matchesStatus &&
    matchesDate &&
    matchesGeneralSearch &&
    matchesPartySearch &&
    matchesCourtSearch &&
    matchesSectionSearch
  );
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export interface PloSixFetchParams {
  searchTerm?: string;
  courtSearch?: string;
  partySearch?: string;
  sectionSearch?: string; // Added field
  selectedStatus?: string;
  selectedDate?: string;
  currentPage: number;
  itemsPerPage: number;
}

// Fetch All / Filtered Applications
export const fetchPloApplications = (
  params: PloSixFetchParams
): Promise<PLOFetchResponse> =>
  genericService.fetchApplications<PLOFormSixData>(
    ENDPOINT,
    params,
    mapPloFormSixBackendToFrontend,
    ploFilterPredicate
  );

// Fetch Single Application by ID
export const fetchPloById = (id: string): Promise<PLOFormSixData> =>
  genericService.fetchApplicationById<PLOFormSixData>(
    ENDPOINT,
    id,
    mapPloFormSixBackendToFrontend
  );

// Save Draft (Single item or Array/Bulk payload)
export const savePloDraft = (
  id: string | null,
  data: Partial<PLOFormSixData> | Partial<PLOFormSixData>[]
) => genericService.saveDraft<PLOFormSixData>(ENDPOINT, id, data as any);

// Delete Form (Soft Delete)
export const deletePloForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);